import * as React from 'react';
import { useCallback, useEffect, useMemo, useState, useLayoutEffect } from 'react';

/** Material UI */
import { Box, Paper, Stack } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { GridRowModes } from '@mui/x-data-grid-pro';
import { randomId } from '@mui/x-data-grid-generator';

/** Icons */
import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';

/** Redux Packages */
import { useDispatch } from 'react-redux';

/** Redux Reducers */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components */
import ThemeButton, { DialogButton } from 'shared/ui/Button';
import { callAPI } from 'shared/api/callApi';
import { MESSAGES } from '../../lib/configUtils';

import { getDetailPanelColumns, isNegativeAmount, negativeValueMessage } from './DutyEntriesDef';
import { dutyEntryDetailGridSx } from 'shared/config/Styles';
import { applyRecalculatedSiblings, flattenRow } from '../../lib/rowSerializers';
import UploadItem from './UploadItem';
import { confirmSojuTierSelection } from '../../lib/sojuTier';

const normalizeTier = (value) => (value ?? '').trim();

/** Renders duty entry detail items with inline editing support. */
export default function DutyEntryDetail({
    userEmail,
    getAccessTokenSilently,
    dutyEntry,
    itemFormulasRows,
    setGridLoading,
    onEntryDetailsAdded,
    onEntryDetailsUpdated,
    onEntryDetailsDeleted,
}) {
    /** Dispatches snackbar notifications for user feedback. */
    const dispatch = useDispatch();

    const [dutyEntryDetailRows, setDutyEntryDetailRows] = useState([]);
    const [rowModesModel, setRowModesModel] = useState({});
    const [uploadItemOpen, setUploadItemOpen] = useState(false);
    const [fileUploadLoading, setFileUploadLoading] = useState(false);

    const dynamicDutyFields = useMemo(() => {
        if (!itemFormulasRows || itemFormulasRows.length === 0) return [];
        return [...new Set(itemFormulasRows.map((row) => row.duty_name))];
    }, [itemFormulasRows]);

    const handleUploadItemClick = () => {
        setUploadItemOpen(true);
    };

    /** Safe handler for rowModesModel changes from DataGrid */
    const handleRowModesModelChange = useCallback((newRowModesModel) => {
        setRowModesModel(newRowModesModel || {});
    }, []);

    /**
     * Adds an empty line item to this entry for the operator to fill in.
     *
     * The row is created on the server first so it has a real id before the operator types into
     * it; if the server refuses, no row appears. A new -CAN line changes the duty of its main
     * item, so the recalculated main rows the server returns are merged in at the same time.
     */
    const handleAddEntryDetail = async () => {
        const id = randomId();

        const newRow = {
            id,
            il_duty_entry_id: dutyEntry.id,
            item_code: '',
            description: '',
            quantity: 0,
            unit_price: 0,
            invoice_amount: 0,
            tier: '',
            manual_calculated: false,
        };

        dynamicDutyFields.forEach((dutyName) => {
            newRow[dutyName] = 0;
        });

        const payload = {
            ...newRow,
            calculated_json: dynamicDutyFields.reduce((acc, key) => {
                acc[key] = 0;
                return acc;
            }, {}),
        };

        dynamicDutyFields.forEach((key) => {
            delete payload[key];
        });

        try {
            const resp = await new Promise((resolve, reject) => {
                callAPI({
                    method: 'post',
                    module: 'duty_calculator',
                    component: 'duty_entry_detail',
                    getAccessTokenSilently,
                    email: userEmail,
                    obj: payload,
                    onInit: () => setGridLoading(true),
                    onFinal: () => setGridLoading(false),
                    onSuccess: (resp) => {
                        // A -CAN line changes its main item's duty — the server sends those main rows back recalculated
                        const { recalculated_siblings, ...createdRow } = resp.data ?? {};
                        const recalculatedRows = recalculated_siblings ?? [];
                        setDutyEntryDetailRows((prev) =>
                            applyRecalculatedSiblings([...prev, createdRow], recalculatedRows),
                        );
                        onEntryDetailsAdded(dutyEntry.id, createdRow);
                        recalculatedRows.forEach((sibling) => onEntryDetailsUpdated?.(dutyEntry.id, sibling));
                        dispatch(
                            setSnackbar({
                                open: true,
                                message: MESSAGES.ADD_SUCCESS,
                                severity: 'success',
                            }),
                        );
                        resolve(resp);
                    },
                    onError: (error) => {
                        dispatch(
                            setSnackbar({
                                open: true,
                                message: error.message,
                                severity: 'error',
                            }),
                        );
                        reject(error);
                    },
                });
            });
        } catch (error) {
            console.error('Failed to add new duty entry detail:', error);
        }
    };

    /** Handler to delete a child item. */
    /**
     * Removes a line item after the operator confirms.
     *
     * A row still being typed into is only dropped from the grid; a saved row is deleted on the
     * server. Deleting a -CAN line restores its main item's duty, so the recalculated main rows
     * the server returns are merged in with the deletion.
     */
    const handleDeleteEntryDetail = (id) => {
        const isConfirmed = window.confirm('Are you sure you want to delete this duty entry detail?');
        if (!isConfirmed) return;
        const isEditMode = rowModesModel?.[id]?.mode === GridRowModes.Edit;

        if (isEditMode) {
            setDutyEntryDetailRows((prev) => prev.filter((row) => row.id !== id));
            setRowModesModel((prevRowModesModel) => {
                return {
                    ...prevRowModesModel,
                    [id]: { mode: GridRowModes.View, ignoreModifications: true },
                };
            });
        } else {
            callAPI({
                method: 'delete',
                module: 'duty_calculator',
                component: 'duty_entry_detail',
                getAccessTokenSilently,
                email: userEmail,
                obj: { id: id },
                onInit: () => setGridLoading(true),
                onFinal: () => setGridLoading(false),
                onSuccess: (resp) => {
                    console.log('delete duty entry detail response', resp);
                    const recalculatedRows = resp?.data?.recalculated_siblings ?? [];
                    setDutyEntryDetailRows((prev) =>
                        applyRecalculatedSiblings(
                            prev.filter((row) => row.id !== id),
                            recalculatedRows,
                        ),
                    );
                    onEntryDetailsDeleted(dutyEntry.id, id);
                    recalculatedRows.forEach((sibling) => onEntryDetailsUpdated?.(dutyEntry.id, sibling));
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: 'Duty entry detail deleted successfully.',
                            severity: 'success',
                        }),
                    );
                },
                onError: (error) => {
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: error.message || 'Failed to delete duty entry detail. Please try again.',
                            severity: 'error',
                        }),
                    );
                },
            });
        }
    };

    /**
     * Saves a line item the operator just edited — a typed cell or a pasted row.
     *
     * Nothing is sent when nothing changed. Negative quantities, amounts and duties are refused
     * before saving, since a paste bypasses the cell-level check. Picking a Tier asks the server
     * whether the year is already past the Tier 1 limit and lets the operator confirm or back out
     * first; cancelling puts the cell back. When the edit moves an item across the -CAN boundary,
     * the main rows the server recalculated are merged in with the saved row.
     */
    const handleProcessEntryDetailRowUpdate = async (newRow, oldRow) => {
        const fieldLabels = { quantity: 'Quantity', invoice_amount: 'Invoice Amount' };
        const negativeField = [...Object.keys(fieldLabels), ...dynamicDutyFields].find((field) =>
            isNegativeAmount(newRow[field]),
        );
        if (negativeField) {
            dispatch(
                setSnackbar({
                    open: true,
                    message: negativeValueMessage(fieldLabels[negativeField] ?? negativeField),
                    severity: 'error',
                }),
            );
            return oldRow;
        }

        const calculated_json = dynamicDutyFields.reduce((acc, key) => {
            if (newRow[key] !== undefined) {
                acc[key] = Number(newRow[key]);
            }
            return acc;
        }, {});

        const normalize = (v) => Number(v ?? 0);
        const isCalculatedChanged = dynamicDutyFields.some((key) => normalize(newRow[key]) !== normalize(oldRow[key]));

        const normalFields = [
            'item_code',
            'item_name',
            'description',
            'quantity',
            'unit_price',
            'invoice_amount',
            'tier',
            'manual_calculated',
        ];
        const isNormalChanged = normalFields.some((key) => newRow[key] !== oldRow[key]);

        if (!isCalculatedChanged && !isNormalChanged) {
            return oldRow;
        }

        // Clearing a Tier has nothing to ask about; picking one is checked against the year's limit
        const nextTier = normalizeTier(newRow.tier);
        if (nextTier && nextTier !== normalizeTier(oldRow.tier)) {
            // The check is its own round trip — without the spinner the operator picks again
            setGridLoading(true);
            let canProceed;
            try {
                canProceed = await confirmSojuTierSelection({
                    ilDutyEntryId: dutyEntry.id,
                    details: [
                        { id: newRow.id, item_code: newRow.item_code, quantity: newRow.quantity, tier: nextTier },
                    ],
                    getAccessTokenSilently,
                    userEmail,
                });
            } finally {
                setGridLoading(false);
            }
            if (!canProceed) return oldRow;
        }

        const payload = {
            ...newRow,
            calculated_json,
        };

        dynamicDutyFields.forEach((key) => {
            delete payload[key];
        });

        try {
            const resp = await new Promise((resolve, reject) => {
                callAPI({
                    method: 'put',
                    module: 'duty_calculator',
                    component: 'duty_entry_detail',
                    getAccessTokenSilently,
                    email: userEmail,
                    obj: payload,
                    onInit: () => setGridLoading(true),
                    onFinal: () => setGridLoading(false),
                    onSuccess: (resp) => {
                        console.log('update duty entry detail response', resp);
                        dispatch(
                            setSnackbar({
                                open: true,
                                message: MESSAGES.UPDATE_SUCCESS,
                                severity: 'success',
                            }),
                        );
                        resolve(resp);
                    },
                    onError: (error) => {
                        dispatch(
                            setSnackbar({
                                open: true,
                                message: error.message,
                                severity: 'error',
                            }),
                        );
                        reject(error);
                    },
                });
            });

            const { recalculated_siblings, ...updatedRow } = resp.data ?? {};
            const recalculatedRows = recalculated_siblings ?? [];
            const flattenedResp = flattenRow(updatedRow);
            setDutyEntryDetailRows((prevRows) =>
                applyRecalculatedSiblings(
                    prevRows.map((row) =>
                        row.id === newRow.id
                            ? {
                                  ...row,
                                  ...flattenedResp,
                              }
                            : row,
                    ),
                    recalculatedRows,
                ),
            );
            onEntryDetailsUpdated(dutyEntry.id, updatedRow);
            recalculatedRows.forEach((sibling) => onEntryDetailsUpdated(dutyEntry.id, sibling));

            return {
                ...newRow,
                ...flattenedResp,
            };
        } catch (error) {
            throw error;
        }
    };

    // The panel shows the entry it was opened for; a list re-read for another reason must not wipe
    // edits in progress, so rows are reloaded only when the operator opens a different entry.
    useLayoutEffect(() => {
        const flattenedRows = dutyEntry?.il_duty_entry_detail?.length
            ? dutyEntry.il_duty_entry_detail.map(flattenRow)
            : [];
        setDutyEntryDetailRows(flattenedRows);
    }, [dutyEntry.id]);

    return (
        <Stack sx={{ p: 2, bgcolor: '#f1f1f1', height: 'auto' }} direction="column">
            <Paper
                sx={{
                    flex: 1,
                    mx: 'auto',
                    width: '100%',
                    p: 0,
                    border: 0,
                    borderRadius: 0,
                    boxShadow: 'none',
                    bgcolor: '#f1f1f1',
                    overflow: 'hidden',
                    height: 'auto',
                }}
            >
                <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <ThemeButton
                            text="NEW"
                            icon={<AddIcon />}
                            sx={{ width: '68px', height: '30px' }}
                            onClick={handleAddEntryDetail}
                        />
                        <DialogButton
                            text="UPLOAD"
                            icon={<FileUploadIcon />}
                            onClick={handleUploadItemClick}
                            sx={{ width: '86px', border: 'solid 1px' }}
                        />
                    </Box>
                </Stack>
                <DataGridPremium
                    rows={dutyEntryDetailRows || []}
                    columns={getDetailPanelColumns({ handleDeleteEntryDetail, itemFormulasRows })}
                    density="compact"
                    editMode="row"
                    rowModesModel={rowModesModel}
                    onRowModesModelChange={handleRowModesModelChange}
                    processRowUpdate={handleProcessEntryDetailRowUpdate}
                    onProcessRowUpdateError={(error) => console.error('Error processing row update:', error)}
                    hideFooter
                    disableColumnMenu
                    disableColumnSorting
                    cellSelection
                    disableRowSelectionOnClick
                    sx={dutyEntryDetailGridSx}
                    aggregationPosition="footer"
                    initialState={{
                        aggregation: {
                            model: {
                                invoice_amount: 'sum',
                                ...dynamicDutyFields.reduce(function (acc, field) {
                                    acc[field] = 'sum';
                                    return acc;
                                }, {}),
                            },
                        },
                    }}
                    getRowHeight={() => 'auto'}
                    isCellEditable={(params) => {
                        if (params.field === 'unit_price') {
                            return false;
                        }
                        const column = params.colDef;
                        const baseEditable = column.editable ?? false;
                        if (!dynamicDutyFields.includes(params.field)) {
                            return baseEditable;
                        }
                        // Duty amounts are typed in only on rows the operator marked M.C (manual)
                        return baseEditable && params.row.manual_calculated === true;
                    }}
                />
            </Paper>
            <UploadItem
                dutyEntry={dutyEntry}
                userEmail={userEmail}
                getAccessTokenSilently={getAccessTokenSilently}
                newOpen={uploadItemOpen}
                setNewOpen={setUploadItemOpen}
                fileUploadLoading={fileUploadLoading}
                setFileUploadLoading={setFileUploadLoading}
                setDutyEntryDetailRows={setDutyEntryDetailRows}
                onEntryDetailsAdded={onEntryDetailsAdded}
                onEntryDetailsUpdated={onEntryDetailsUpdated}
                setGridLoading={setGridLoading}
            />
        </Stack>
    );
}
